"use client";

import { useMemo, useState } from "react";
import { MessageCircleMore } from "lucide-react";
import { PackageTierCards } from "@/components/trip/PackageTierCards";
import { GroupPricingDisplay } from "@/components/trip/GroupPricingDisplay";
import { CostDetails } from "@/components/trip/CostDetails";
import { TierBookingSidebar } from "@/components/trip/TierBookingSidebar";
import { useBookingPrice } from "@/components/trip/BookingPrice";
import { resolveTierPrice } from "@/lib/format";
import type {
  PackageTier,
  Departure,
  GroupPrice,
  PackageAddon,
  Media,
} from "@/lib/dto";

type Props = {
  tiers: Record<string, PackageTier> | null | undefined;
  currency: string;
  packageTitle: string;
  packageSlug: string;
  packageId?: number;
  departures?: Departure[];
  pricing?: {
    group_prices: GroupPrice[];
    tiers: Record<string, PackageTier> | null;
  };
  addons?: PackageAddon[];
  featuredImage?: Media | null;
  duration?: number;
  durationUnit?: string;
  onPriceChange?: (tierKey: string, price: number) => void;
  /** Rich-text booking/pricing policy note shown under the departure calendar. */
  departureNote?: string | null;
};

/**
 * "Cost, Inclusions & Availability" section — matches production's real
 * layout exactly (confirmed via the live DOM): a left vertical tier rail
 * beside a single full-width panel that stacks the tier abstract
 * (blockquote), the Group-Size Discounts table, the Inclusions/Exclusions
 * lists, and the departure/booking calendar.
 *
 * The previous build used horizontal price-card tabs over a 7/5 split with
 * a nested booking sidebar, which production doesn't have.
 */
export function PackageTiers({
  tiers,
  currency,
  packageTitle,
  packageSlug,
  packageId,
  departures,
  pricing,
  addons,
  featuredImage,
  duration,
  durationUnit,
  onPriceChange,
  departureNote,
}: Props) {
  const entries = useMemo(
    () =>
      Object.entries(tiers ?? {}).sort(
        ([, a], [, b]) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
      ),
    [tiers],
  );

  const [activeKey, setActiveKey] = useState<string>(entries[0]?.[0] ?? "");
  const { setTierPrice } = useBookingPrice();

  if (entries.length === 0) return null;

  const handleSelect = (key: string) => {
    if (key === activeKey) return;
    setActiveKey(key);
    const tier = tiers![key];
    if (tier) {
      const price = resolveTierPrice(tier);
      setTierPrice(price);
      onPriceChange?.(key, price);
    }
  };

  const activeTier = tiers![activeKey];
  if (!activeTier) return null;

  const tierCurrency = activeTier.currency || currency;
  const groupPrices =
    activeTier.groupPrices ?? activeTier.group_prices ?? pricing?.group_prices;

  return (
    <section id="package-options" className="scroll-mt-32">
      <div className="mb-3">
        <h2 className="font-secondary text-lg font-extrabold text-headings md:text-xl lg:text-[1.675rem]">
          Cost, Inclusions &amp; Availability
        </h2>
      </div>

      <div className="flex flex-col lg:flex-row">
        {entries.length > 1 ? (
          <aside className="w-full pt-6 sm:w-[120px] lg:flex-shrink-0">
            <PackageTierCards
              tiers={tiers!}
              selectedKey={activeKey}
              onSelect={handleSelect}
            />
          </aside>
        ) : null}

        <div
          id="package-tier-panel"
          role="tabpanel"
          aria-labelledby={`package-tier-tab-${activeKey}`}
          className="relative flex-1 lg:rounded-lg lg:border lg:border-line lg:bg-white lg:p-6"
        >
          {activeTier.abstract ? (
            <div className="mb-3">
              <blockquote className="rounded border-0 bg-[#EFF0F1] px-5 py-6 font-secondary text-sm font-semibold leading-[1.6] md:text-[1.06275rem]">
                {activeTier.abstract}
              </blockquote>
            </div>
          ) : null}

          {groupPrices && groupPrices.length > 1 ? (
            <div className="mb-6">
              <div className="mb-3">
                <h3 className="text-lg font-extrabold text-headings capitalize">
                  <svg className="mr-1 inline-block h-12 w-12 text-muted" aria-hidden>
                    <use xlinkHref="/icons.svg#group_price" />
                  </svg>{" "}
                  Group-Size Discounts
                </h3>
              </div>
              <GroupPricingDisplay
                groupPrices={groupPrices}
                currency={tierCurrency}
              />
            </div>
          ) : null}

          <CostDetails
            inclusions={activeTier.costInclude ?? activeTier.cost_include}
            exclusions={activeTier.costExclude ?? activeTier.cost_exclude}
          />

          <div id="departure-dates" className="mt-6 scroll-mt-32">
            {departureNote ? (
              <div className="bg-[#EFF0F1] mb-6 p-6 rounded-md text-md font-secondary">
                <h3 className="mb-3 text-[1.125rem] font-display font-extrabold">
                  Note to Travelers
                  <MessageCircleMore
                    fill="#14BF60"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    size={20}
                    className="align-text-bottom ml-1 inline-block"
                    aria-hidden
                  />{" "}
                </h3>
                <article
                  dangerouslySetInnerHTML={{ __html: departureNote }}
                  className="text-text leading-[1.6]"
                ></article>
              </div>
            ) : null}
            <TierBookingSidebar
              key={activeKey}
              tier={activeTier}
              tierKey={activeKey}
              currency={tierCurrency}
              departures={departures}
              packageTitle={packageTitle}
              packageSlug={packageSlug}
              packageId={packageId}
              pricing={pricing}
              addons={addons}
              featuredImage={featuredImage}
              duration={duration}
              durationUnit={durationUnit}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
